import { useDispatch, useSelector } from 'react-redux';

import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type ReservationClass } from '../domain/resaClass';

import { setLoader, toggleToast, unsetLoader } from '@/common/application/toastSlice';
import { RequestTypeEnum } from '@/common/domain/toast';
import { createEmptyPromise, type SelfResolvingPromise } from '@/group/data/groupStore';
import { type Group } from '@/group/domain/group';
import useSubscriptionApi from '@/subscription/data/subscriptionApi';


export interface SubscriptionStore {
    isLoading: boolean;
    keyLoader: {[key: string]: Promise<ReservationClass>}
    contextReservationClassMap: {[key: string]: ReservationClass},
}

const initialState: SubscriptionStore = {
    contextReservationClassMap: {},
    isLoading: false,
    keyLoader: {},
};

const sideState = {
    admKeyLoader: {} as {[key: string]: SelfResolvingPromise<Group>},
    keyLoader: {} as {[key: string]: SelfResolvingPromise<ReservationClass>}
};

const subscriptionStore = createSlice({
    initialState,
    name: 'subscriptionStore',
    reducers: {
        clearStore() {
            return initialState;
        },
        deleteReservationClass(state, action: PayloadAction<number | string>) {
            if (state.contextReservationClassMap[String(action.payload)]) delete state.contextReservationClassMap[String(action.payload)];
        },
        setAdmLoadingKey(state, action: PayloadAction<string>) {
            sideState.admKeyLoader[action.payload] = createEmptyPromise();
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        clearLoadingKey(state, action: PayloadAction<string>) {
            delete sideState.keyLoader[action.payload];
        },
        setLoadingKey(state, action: PayloadAction<string>) {
            sideState.keyLoader[action.payload] = createEmptyPromise();
        },
        failLoadingKey(state, action: PayloadAction<string>) {
            sideState.keyLoader[action.payload].reject(new Error('fail to load reservationclass'));
            delete sideState.keyLoader[action.payload];
        },
        unsetAdmLoadingKey(state, action: PayloadAction<{key: string, grp: Group}>) {
            if (action.payload.grp.id) {
                sideState.admKeyLoader[action.payload.key].resolve({...action.payload.grp});
                delete sideState.admKeyLoader[action.payload.key];
            }
        },
        upsertReservationClass(state, action: PayloadAction<ReservationClass>) {

            const rsacl = action.payload;
            if (rsacl.id) state.contextReservationClassMap[rsacl.id] = {...rsacl};
            if (rsacl.id && sideState.keyLoader[rsacl.id]) {
                sideState.keyLoader[rsacl.id].resolve({...rsacl});
                delete sideState.keyLoader[rsacl.id];
            }
            if (rsacl.clientId && sideState.keyLoader['client_' + rsacl.clientId]) {
                sideState.keyLoader['client_' + rsacl.clientId].resolve({...rsacl});
                delete sideState.keyLoader['client_' + rsacl.clientId];
            }
        },

        // TODO
        upsertReservationClasses(state, action: PayloadAction<ReservationClass[]>) {
            const newReservationClassesList:{[key: string]: ReservationClass} = {...state.contextReservationClassMap};
            for (const rsacl of action.payload) {
                if (rsacl.id) newReservationClassesList[rsacl.id] = { ...rsacl};
            }

            state.contextReservationClassMap = newReservationClassesList;
        }
    },
    selectors: {
        
        getReservationClasses: createSelector([state => state.subscriptionStore?.contextReservationClassMap], (contextReservationClassMap): ReservationClass[] => {
            return contextReservationClassMap ? Object.values<ReservationClass>(contextReservationClassMap) : [];
        }),
        getReservationClassById: createSelector([state => state.subscriptionStore?.contextReservationClassMap], (contextReservationClassMap): (id: string | number) => ReservationClass | null => {
            return (id: string | number) => contextReservationClassMap ? contextReservationClassMap[String(id)] : null;
        }),
        getReservationClassByClient: createSelector([state => state.subscriptionStore?.contextReservationClassMap], (contextReservationClassMap): (id: string | number) => ReservationClass | null => {
            return (id: string | number) => {
                if (!contextReservationClassMap) return null;

                const rsaClass = Object.values<ReservationClass>(contextReservationClassMap).find(item => item.clientId === String(id));

                if (!rsaClass) return null;
                return contextReservationClassMap[rsaClass.id] ?? null;
            };
        }) 
    }
});

export const { clearStore, upsertReservationClasses, setAdmLoadingKey, unsetAdmLoadingKey, failLoadingKey, clearLoadingKey, setLoadingKey, setLoading, upsertReservationClass, deleteReservationClass } = subscriptionStore.actions;
export const { getReservationClassById } = subscriptionStore.selectors;
export default subscriptionStore.reducer;

export function useSubscriptionStore() {

    const { apiGetResaClass, apiGetResaClasses, apiGetResaClassByClient } = useSubscriptionApi();
    
    
    const dispatch = useDispatch();
    const { getReservationClassById, getReservationClasses, getReservationClassByClient } = subscriptionStore.getSelectors();
    const findResaClassById = useSelector(getReservationClassById);
    const findResaClasses = useSelector(getReservationClasses);
    const findResaClassByClient = useSelector(getReservationClassByClient);

    return {
        async deleteReservationClass(id: number | string) {
            return dispatch(deleteReservationClass(id));
        },
       
        async getReservationClasses(force: boolean = false) {
            const foundrsacls = findResaClasses;

            if (foundrsacls.length > 0 && !force) {
                return foundrsacls;
            } else {
                try {
                    dispatch(setLoader());

                    const rsaclsResp = await apiGetResaClasses();

                    if (rsaclsResp.status.code < 400 && Array.isArray(rsaclsResp.data)) {
                        dispatch(upsertReservationClasses(rsaclsResp?.data));

                    }
                    dispatch(unsetLoader());
                    return rsaclsResp?.data;
                } catch (error: any) {
                    dispatch(unsetLoader());

                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getCompanies}));
                    }
                }

            }
        },

        async getReservationClassById(id: string | number, force: boolean = false) {

            const loadRsaClFromApi = async () => {
                try {
                    dispatch(setLoadingKey(String(id)));

                    dispatch(setLoader());

                    const reservationclassResp = await apiGetResaClass(id);

                    if (reservationclassResp.status.code < 400 && reservationclassResp.data.id) {
                        dispatch(upsertReservationClass(reservationclassResp?.data));
                        dispatch(unsetLoader());

                        return reservationclassResp.data;
                    }
                } catch (error: any) {
                    dispatch(unsetLoader());

                    dispatch(failLoadingKey(String(id)));

                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getCompanies}));
                    }
                }
            };
            if (!force) {
                const rsaclg = findResaClassById(id);

                if (rsaclg?.id === (id)) return rsaclg;
                if (typeof sideState.keyLoader[String(id)] !== 'undefined') return await sideState.keyLoader[String(id)];

                return await loadRsaClFromApi();
            } else {

                return await loadRsaClFromApi();
            }

        },

        async getReservationClassByClient(id: string | number, force: boolean = false) {

            const loadRsaClFromApi = async () => {
                try {
                    dispatch(setLoadingKey(String(id)));

                    dispatch(setLoader());

                    const reservationclassResp = await apiGetResaClassByClient(id);

                    if (reservationclassResp.data.id) {
                        dispatch(upsertReservationClass(reservationclassResp?.data));
                        dispatch(unsetLoader());

                        return reservationclassResp.data;
                    } else {
                        dispatch(unsetLoader());
                    }
                } catch (error: any) {
                    dispatch(unsetLoader());

                    dispatch(failLoadingKey(String(id)));

                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getCompanies}));
                    }
                }
            };
            if (!force) {
                const rsaclg = findResaClassByClient(id);

                if (rsaclg?.clientId === String(id)) return rsaclg;
                if (typeof sideState.keyLoader['client_' + String(id)] !== 'undefined') return await sideState.keyLoader['client_' + String(id)];

                return await loadRsaClFromApi();
            } else {

                return await loadRsaClFromApi();
            }

        }
    };
}